// ALL raw zeno v1 payload parsing lives here. When the ADR-0016 envelope
// and ADR-0017 parameter array land, they land in this file — the UI keeps
// consuming the clean types from types.ts unchanged.
//
// Raw shapes mirrored from packages/types/src/{zeno,aggregator}.d.ts (kept
// local so this module stays the single authority on the wire format).

import type {
  EventSnapshot,
  MarketSnapshot,
  MarketStatus,
  PricePoint,
  SettledEntry,
} from './types';

// ── Raw wire types (v1) ────────────────────────────────────────────────

/** MashPrice tuple: [price, timestamp, manualMode?, deferredUntil?] */
type RawPrice = (string | number | null)[];

interface RawCompetitor {
  name?: string;
  shortName?: string;
  symbol?: string;
  logoUrl?: string;
}

interface RawEvent {
  eventId?: string;
  status?: string;
  startTime?: string | number | Date;
  liveTime?: string | null;
  score?: string | null;
  meta?: {
    sport?: string;
    eventGroup?: string;
    eventGroupShort?: string;
    eventImageUrl?: string | null;
    category?: string;
    competitors?: { home?: RawCompetitor; away?: RawCompetitor };
  };
}

interface RawMarket {
  index?: number;
  name?: string;
  displayName?: string;
  symbol?: string;
  status?: string;
  price?: RawPrice;
  priceDirection?: string;
  transformPrice?: number | null;
  soFar?: string | null;
}

interface RawEventWithMarkets extends RawEvent {
  markets?: { [marketIndex: string]: RawMarket };
}

// ── Parsing ────────────────────────────────────────────────────────────

const KNOWN_STATUSES: MarketStatus[] = [
  'upcoming',
  'in_play',
  'suspended',
  'closed',
  'settled',
];

const asStatus = (raw: string | undefined): MarketStatus =>
  KNOWN_STATUSES.includes(raw as MarketStatus)
    ? (raw as MarketStatus)
    : 'suspended';

export function parsePrice(raw: RawPrice | undefined): {
  price: number | null;
  timestamp: number;
} {
  if (!Array.isArray(raw) || raw.length < 2) {
    return { price: null, timestamp: 0 };
  }
  const [priceStr, ts] = raw;
  const price =
    priceStr === null || priceStr === undefined ? null : Number(priceStr);
  return {
    price: price !== null && Number.isFinite(price) ? price : null,
    timestamp: Number(ts) || 0,
  };
}

const competitor = (
  raw: RawCompetitor | undefined,
  fallback: string,
): { name: string; symbol: string; shortName?: string; logoUrl?: string } => ({
  name: raw?.name ?? fallback,
  symbol: raw?.symbol ?? fallback.slice(0, 3).toUpperCase(),
  shortName: raw?.shortName,
  logoUrl: raw?.logoUrl,
});

export function parseEvent(raw: RawEvent): EventSnapshot {
  const meta = raw.meta ?? {};
  return {
    eventId: raw.eventId ?? '',
    sport: meta.sport ?? '',
    league: meta.eventGroup ?? '',
    leagueShort: meta.eventGroupShort ?? '',
    category: meta.category ?? '',
    imageUrl: meta.eventImageUrl ?? null,
    status: asStatus(raw.status),
    startTime: new Date(raw.startTime ?? 0).getTime(),
    liveTime: raw.liveTime ?? null,
    score: raw.score ?? null,
    home: competitor(meta.competitors?.home, 'Home'),
    away: competitor(meta.competitors?.away, 'Away'),
  };
}

export function parseMarket(raw: RawMarket, eventId: string): MarketSnapshot {
  const { price, timestamp } = parsePrice(raw?.price);
  return {
    index: raw?.index ?? -1,
    eventId,
    name: raw?.name ?? '',
    displayName: raw?.displayName || raw?.name || '',
    symbol: raw?.symbol ?? '',
    status: asStatus(raw?.status),
    price,
    priceTimestamp: timestamp,
    priceDirection: raw?.priceDirection === 'down' ? 'down' : 'up',
    transformPrice: raw?.transformPrice ?? 0,
    soFar: raw?.soFar ?? null,
    history: [],
  };
}

export function parseEventWithMarkets(raw: RawEventWithMarkets): {
  event: EventSnapshot;
  markets: MarketSnapshot[];
} {
  const event = parseEvent(raw);
  const markets = Object.values(raw.markets ?? {})
    .filter((m) => typeof m?.index === 'number')
    .map((m) => parseMarket(m, event.eventId));
  return { event, markets };
}

/** History bound shared by the rolling delta window and hydration paths. */
const HISTORY_BOUND = 120;

/** GET /markets/:index/chart rows. Prod (aggregator) writes epoch-ms
 * timestamps, the zeno-seed fixtures epoch-s — normalize both; drop garbage. */
export function parseChartPoints(
  raw: { timestamp: number; price: number }[] | null | undefined,
): PricePoint[] {
  return (raw ?? [])
    .filter(
      (pt) =>
        Number.isFinite(pt?.price) &&
        Number.isFinite(pt?.timestamp) &&
        pt.timestamp > 0,
    )
    .map((pt) => ({
      t: pt.timestamp < 1e12 ? pt.timestamp * 1000 : pt.timestamp,
      p: pt.price,
    }))
    .slice(-HISTORY_BOUND);
}

/** Mongo MashPrice tuples [price, ts-ms] → PricePoint[] (archive sparklines). */
function parsePriceTuples(prices: RawPrice[] | undefined): PricePoint[] {
  return (prices ?? [])
    .filter(
      (pr) =>
        Number.isFinite(Number(pr?.[0])) && Number.isFinite(Number(pr?.[1])),
    )
    .map((pr) => ({ p: Number(pr[0]), t: Number(pr[1]) }))
    .slice(-HISTORY_BOUND);
}

/** GET /markets/past response: markets keyed by index, each with its event */
export function parsePastMarkets(raw: {
  [marketIndex: string]: RawMarket & { event?: RawEvent; prices?: RawPrice[] };
}): SettledEntry[] {
  return Object.values(raw ?? {})
    .filter((m) => typeof m?.index === 'number' && m?.event)
    .map((m) => {
      const entry = {
        market: parseMarket(m, m.event!.eventId ?? ''),
        event: parseEvent(m.event!),
      };
      entry.market.history = parsePriceTuples(m.prices);
      return entry;
    })
    .sort((a, b) => b.event.startTime - a.event.startTime);
}

// ── WS frames ──────────────────────────────────────────────────────────

export type FeedDelta =
  | { channel: 'market:updated'; market: RawMarket }
  | { channel: 'event:update' | 'event:added'; event: RawEventWithMarkets }
  | {
      channel: 'event:removed';
      eventId: string;
      marketIndexes: number[];
    };

/**
 * Server frames: broadcasts are `{channel, marketIndex?, data}`; subscribe
 * acks / pongs / errors carry a `type` field and no channel → null.
 */
export function parseWsMessage(raw: string): FeedDelta | null {
  let msg: {
    channel?: string;
    data?: {
      market?: RawMarket;
      event?: RawEventWithMarkets;
      eventId?: string;
      marketIndexes?: number[];
    };
  };
  try {
    msg = JSON.parse(raw);
  } catch (err) {
    console.error('feed: malformed ws frame', err);
    return null;
  }
  const d = msg?.data;
  if (msg?.channel === 'market:updated' && d?.market) {
    return { channel: 'market:updated', market: d.market };
  }
  if (
    (msg?.channel === 'event:update' || msg?.channel === 'event:added') &&
    d?.event
  ) {
    return { channel: msg.channel, event: d.event };
  }
  if (msg?.channel === 'event:removed') {
    return {
      channel: 'event:removed',
      eventId: d?.eventId ?? '',
      marketIndexes: d?.marketIndexes ?? [],
    };
  }
  return null;
}

/** rolling history append — newest last, bounded */
export const appendHistory = (
  prev: PricePoint[],
  price: number | null,
  timestamp: number,
): PricePoint[] =>
  price === null || prev.at(-1)?.p === price
    ? prev
    : [...prev, { t: timestamp, p: price }].slice(-120);
